import { createGridMesh } from "../../assetBuilders/grid";
import { materialForColor } from "../../assetBuilders/materials";
import type * as components from "../../components";
import { BabylonState } from "../../state";

/**
 * Generates the grid mesh based on the current grid spec and camera viewing
 * range, and swaps it in without a gap.
 *
 * A freshly built mesh cannot draw on its first frame: its material's effect
 * compiles in parallel, and Babylon skips the mesh until it is ready. Disposing
 * the old grid first therefore left one frame with no grid at all, which showed
 * as the grid blinking off on every rebuild. So the grid being drawn stays, as
 * {@link components.singletons.Grid.previous}, until its replacement reports
 * ready, and only then is it disposed and the replacement made visible.
 * Checked on every poll, not only on a rebuild, since readiness arrives
 * between polls.
 */
export function reloadStaleGrid(state: BabylonState) {
  const grid = state.sceneConfig.grid;
  if (!grid) {
    return;
  }
  if (grid.load.state === "stale") {
    rebuildGrid(grid, state);
  }
  // `isReady` asks the material to compile its effect if it has not yet, so a
  // replacement waiting here is what brings its own readiness about.
  if (grid.previous && grid.mesh?.isReady(true)) {
    grid.previous.mesh.dispose();
    grid.previous.material?.dispose();
    grid.previous = undefined;
    grid.mesh.isVisible = true;
  }
}

function rebuildGrid(grid: components.singletons.Grid, state: BabylonState) {
  // Retire the grid being drawn. A replacement still waiting to draw is
  // dropped instead, since nobody has seen it, and the drawn grid stays.
  if (grid.previous) {
    grid.mesh?.dispose();
    grid.material?.dispose();
  } else if (grid.mesh) {
    grid.previous = { mesh: grid.mesh, material: grid.material };
  }
  grid.mesh = undefined;
  grid.material = undefined;
  // TODO: if the mesh rebuild slows down the main thread, split it up into units of work here.

  // Recompute the grid size.
  // The grid should extend all the way to the draw distance limit, determined by the fog rn.
  // Formulas for fog transparency available here:
  // https://learn.microsoft.com/en-us/previous-versions/windows/desktop/bb324452(v=vs.85)
  // Anything further than the distance where the fog becomes nearly opaque
  // is effectively not visible.
  const NEARLY_OPAQUE = 0.01;
  // Fog transparency of 0 means only fog is visible, which happens at infinite distance.
  // Therefore, solve for fog transparency close to zero to set a cutoff where drawing things
  // (like the infinite grid) doesn't make sense anymore
  const fogDensity = state.context.scene.fogDensity;
  const maxDrawDistance = Math.log(1 / NEARLY_OPAQUE) / fogDensity;
  const scale = grid.spec.scale;
  // Don't make a grid if the scale is not set right.
  if (
    scale === null ||
    scale === undefined ||
    !Number.isFinite(scale) ||
    scale <= 0
  ) {
    // No grid to swap to, so the one being drawn goes too.
    grid.previous?.mesh.dispose();
    grid.previous?.material?.dispose();
    grid.previous = undefined;
    grid.load.state = "loaded";
    return;
  }
  const mesh = createGridMesh(
    "sceneConfig.grid.mesh",
    scale,
    // The grid runs from max draw distance on one side to max distance on the
    // opposite side of the camera.
    // No need to continue the grid beyond the maximum draw distance.
    // Also, set a hard limit of 2k lines.
    Math.min(maxDrawDistance * 2, 2e3 * scale),
    state.context.scene,
  );
  const material = materialForColor(
    "sceneConfig.grid.material",
    grid.spec.color,
    state.context.scene,
  );
  mesh.material = material;
  // Drawn once it can be, in place of the grid it replaces; at once when there
  // is none.
  mesh.isVisible = grid.previous === undefined;

  grid.material = material;
  grid.mesh = mesh;
  grid.load.state = "loaded";
}
